import { type ChangeEvent, memo, useCallback, useContext, useEffect, useId, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { FieldStatus } from '@m/alchemy-ui/FieldStatus';
import { Input } from '@m/alchemy-ui/Input';
import { Select } from '@m/alchemy-ui/Select';
import { LinkTargetTypeEnum } from '../../../../common/core-api';
import { TranslatableInput } from '../../../challenge-management/components';
import {
  buildButtonConfig,
  CATEGORY_DISPLAY_PREFIX,
  displayToTargetType,
  EMPTY_BUTTON_CONFIG,
  getChallengeUrl,
  getPageUrl,
  PAGE_DISPLAY_PREFIX,
  type PanelButtonConfig
} from '../../common/button-link-config';
import { PAGE_OBJECT_TYPE } from '../../common/const';
import { PanelEditorDataContext, type PanelEditorPage } from '../../common/panel-editor-data';
import { panelValidationMessages } from '../../common/panel-validation';
import { PanelEditorLabel, PanelEditorSection } from '../../CustomPage.styled';

const messages = defineMessages({
  button: {
    id: 'button',
    defaultMessage: 'Button'
  },
  link: {
    id: 'link',
    defaultMessage: 'Link'
  },
  none: {
    id: 'none',
    defaultMessage: 'None'
  },
  submitIdea: {
    id: 'submitIdea',
    defaultMessage: 'Submit idea'
  },
  challengesDirectory: {
    id: 'challengesDirectory',
    defaultMessage: 'Challenges directory'
  },
  navigateToChallenge: {
    id: 'editCustomPageNavigateToChallenge',
    defaultMessage: 'Navigate to challenge'
  },
  navigateToPage: {
    id: 'editCustomPageNavigateToPage',
    defaultMessage: 'Navigate to page'
  },
  custom: {
    id: 'custom',
    defaultMessage: 'Custom'
  },
  challenge: {
    id: 'challenge',
    defaultMessage: 'Challenge'
  },
  page: {
    id: 'editCustomPagePageSelectLabel',
    defaultMessage: 'Page'
  },
  buttonText: {
    id: 'buttonText',
    defaultMessage: 'Button text'
  },
  linkTitle: {
    id: 'linkTitle',
    defaultMessage: 'Link title'
  },
  linkUrl: {
    id: 'linkUrl',
    defaultMessage: 'Link URL'
  },
  unnamedPage: {
    id: 'unnamedPage',
    defaultMessage: 'Unnamed page'
  }
});

export type LinkConfigVariant = 'button' | 'link';

interface LinkConfigSelectProps {
  readonly panelId: string;
  readonly buttonConfig: PanelButtonConfig | null;
  // 'button' renders the text-panel wording (Button / Button text), 'link' the image-panel one.
  readonly variant: LinkConfigVariant;
  readonly showPanelErrors: boolean;
  readonly onChange: (buttonConfig: PanelButtonConfig) => void;
}

const LinkConfigSelectComponent = ({
  panelId,
  buttonConfig,
  variant,
  showPanelErrors,
  onChange
}: LinkConfigSelectProps) => {
  const intl = useIntl();
  const id = useId();
  const { challenges, pages } = useContext(PanelEditorDataContext);
  const targetLabelId = `link-config-target-label-${id}`;
  const secondaryLabelId = `link-config-secondary-label-${id}`;
  const customTextInputId = `link-config-text-${id}`;
  const externalUrlInputId = `link-config-external-url-${id}`;

  const config = buttonConfig ?? EMPTY_BUTTON_CONFIG;
  const target = displayToTargetType(config.display);

  const getPageLabel = useCallback(
    (page: PanelEditorPage) => page.pageNavTitle || page.title || intl.formatMessage(messages.unnamedPage),
    [intl]
  );

  const targetOptions = useMemo(
    () => [
      { value: LinkTargetTypeEnum.NONE as string, label: intl.formatMessage(messages.none) },
      { value: LinkTargetTypeEnum.SUBMIT_IDEA as string, label: intl.formatMessage(messages.submitIdea) },
      {
        value: LinkTargetTypeEnum.CHALLENGES_DIRECTORY as string,
        label: intl.formatMessage(messages.challengesDirectory)
      },
      { value: LinkTargetTypeEnum.CHALLENGE as string, label: intl.formatMessage(messages.navigateToChallenge) },
      { value: LinkTargetTypeEnum.PAGE as string, label: intl.formatMessage(messages.navigateToPage) },
      { value: LinkTargetTypeEnum.CUSTOM_EXTERNAL as string, label: intl.formatMessage(messages.custom) }
    ],
    [intl]
  );

  const challengeOptions = useMemo(
    () =>
      challenges.map((challenge) => ({ value: `${CATEGORY_DISPLAY_PREFIX}${challenge.id}`, label: challenge.title })),
    [challenges]
  );

  const pageOptions = useMemo(
    () => pages.map((page) => ({ value: `${PAGE_DISPLAY_PREFIX}${page.id}`, label: getPageLabel(page) })),
    [pages, getPageLabel]
  );

  // The stored target can be missing from the fetched lists (e.g. archived/hidden since saving);
  // keep it selectable through an entry built from the stored buttonConfig itself.
  const appendStoredTarget = useCallback(
    (options: { value: string; label: string }[], prefix: string) => {
      if (config.display === prefix || options.some((option) => option.value === config.display)) {
        return options;
      }
      return [...options, { value: config.display, label: config.text ?? '' }];
    },
    [config.display, config.text]
  );

  const challengeTargetOptions = useMemo(
    () =>
      target === LinkTargetTypeEnum.CHALLENGE
        ? appendStoredTarget(challengeOptions, CATEGORY_DISPLAY_PREFIX)
        : challengeOptions,
    [target, challengeOptions, appendStoredTarget]
  );

  const pageTargetOptions = useMemo(
    () => (target === LinkTargetTypeEnum.PAGE ? appendStoredTarget(pageOptions, PAGE_DISPLAY_PREFIX) : pageOptions),
    [target, pageOptions, appendStoredTarget]
  );

  /*
   * Legacy parity: buttonConfig.text mirrors the current challenge/page title (the public
   * renderer displays it). Sync it from the freshly fetched lists when the stored value drifted.
   */
  useEffect(() => {
    if (target === LinkTargetTypeEnum.CHALLENGE) {
      const selected = challenges.find((challenge) => `${CATEGORY_DISPLAY_PREFIX}${challenge.id}` === config.display);
      if (selected && config.text !== selected.title) {
        onChange(
          buildButtonConfig(config, {
            targetType: LinkTargetTypeEnum.CHALLENGE,
            display: config.display,
            text: selected.title,
            url: getChallengeUrl(selected.id)
          })
        );
      }
    }
    if (target === LinkTargetTypeEnum.PAGE) {
      const selected = pages.find((page) => `${PAGE_DISPLAY_PREFIX}${page.id}` === config.display);
      if (selected && config.text !== getPageLabel(selected)) {
        onChange(
          buildButtonConfig(config, {
            targetType: LinkTargetTypeEnum.PAGE,
            display: config.display,
            text: getPageLabel(selected),
            url: getPageUrl(selected)
          })
        );
      }
    }
  }, [target, config, challenges, pages, getPageLabel, onChange]);

  const handleTargetChange = useCallback(
    (option: { value: string }) =>
      onChange(buildButtonConfig(config, { targetType: option.value as LinkTargetTypeEnum })),
    [config, onChange]
  );

  const handleSubmitIdeaCategoryChange = useCallback(
    (option: { value: string }) =>
      onChange(buildButtonConfig(config, { targetType: LinkTargetTypeEnum.SUBMIT_IDEA, category: option.value })),
    [config, onChange]
  );

  const handleChallengeTargetChange = useCallback(
    (option: { value: string }) => {
      const selected = challenges.find((challenge) => `${CATEGORY_DISPLAY_PREFIX}${challenge.id}` === option.value);
      onChange(
        buildButtonConfig(config, {
          targetType: LinkTargetTypeEnum.CHALLENGE,
          display: option.value,
          ...(selected ? { text: selected.title, url: getChallengeUrl(selected.id) } : {})
        })
      );
    },
    [config, challenges, onChange]
  );

  const handlePageTargetChange = useCallback(
    (option: { value: string }) => {
      const selected = pages.find((page) => `${PAGE_DISPLAY_PREFIX}${page.id}` === option.value);
      onChange(
        buildButtonConfig(config, {
          targetType: LinkTargetTypeEnum.PAGE,
          display: option.value,
          ...(selected ? { text: getPageLabel(selected), url: getPageUrl(selected) } : {})
        })
      );
    },
    [config, pages, getPageLabel, onChange]
  );

  const handleCustomTextChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      onChange(
        buildButtonConfig(config, {
          targetType: LinkTargetTypeEnum.CUSTOM_EXTERNAL,
          text: event.target.value,
          externalUrl: config.externalUrl ?? ''
        })
      ),
    [config, onChange]
  );

  const handleExternalUrlChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      onChange(
        buildButtonConfig(config, {
          targetType: LinkTargetTypeEnum.CUSTOM_EXTERNAL,
          externalUrl: event.target.value,
          text: config.text ?? null
        })
      ),
    [config, onChange]
  );

  const showSelectValueError =
    showPanelErrors && (config.display === CATEGORY_DISPLAY_PREFIX || config.display === PAGE_DISPLAY_PREFIX);
  const selectValueError = showSelectValueError && (
    <FieldStatus level="error" message={intl.formatMessage(panelValidationMessages.selectValueRequired)} />
  );

  return (
    <>
      <PanelEditorSection>
        <PanelEditorLabel id={targetLabelId}>
          {intl.formatMessage(variant === 'button' ? messages.button : messages.link)}
        </PanelEditorLabel>
        <Select
          labelId={targetLabelId}
          isFullWidth
          value={target}
          options={targetOptions}
          onChange={handleTargetChange}
        />
      </PanelEditorSection>

      {target === LinkTargetTypeEnum.SUBMIT_IDEA && (
        <PanelEditorSection>
          <PanelEditorLabel id={secondaryLabelId}>{intl.formatMessage(messages.challenge)}</PanelEditorLabel>
          <Select
            labelId={secondaryLabelId}
            isFullWidth
            value={config.category ?? ''}
            options={challengeOptions}
            onChange={handleSubmitIdeaCategoryChange}
          />
        </PanelEditorSection>
      )}

      {target === LinkTargetTypeEnum.CHALLENGE && (
        <PanelEditorSection>
          <PanelEditorLabel id={secondaryLabelId}>{intl.formatMessage(messages.challenge)}</PanelEditorLabel>
          <Select
            labelId={secondaryLabelId}
            isFullWidth
            value={config.display === CATEGORY_DISPLAY_PREFIX ? '' : config.display}
            options={challengeTargetOptions}
            onChange={handleChallengeTargetChange}
          />
          {selectValueError}
        </PanelEditorSection>
      )}

      {target === LinkTargetTypeEnum.PAGE && (
        <PanelEditorSection>
          <PanelEditorLabel id={secondaryLabelId}>{intl.formatMessage(messages.page)}</PanelEditorLabel>
          <Select
            labelId={secondaryLabelId}
            isFullWidth
            value={config.display === PAGE_DISPLAY_PREFIX ? '' : config.display}
            options={pageTargetOptions}
            onChange={handlePageTargetChange}
          />
          {selectValueError}
        </PanelEditorSection>
      )}

      {target === LinkTargetTypeEnum.CUSTOM_EXTERNAL && (
        <>
          <PanelEditorSection>
            <PanelEditorLabel as="label" htmlFor={customTextInputId}>
              {intl.formatMessage(variant === 'button' ? messages.buttonText : messages.linkTitle)}
            </PanelEditorLabel>
            <TranslatableInput
              type={PAGE_OBJECT_TYPE}
              field={`buttontext--${panelId}`}
              translateId={`buttontext--${panelId}`}
              fieldLabel={intl.formatMessage(variant === 'button' ? messages.buttonText : messages.linkTitle)}
              id={customTextInputId}
            >
              <Input
                id={customTextInputId}
                name={`buttontext--${panelId}`}
                value={config.text ?? ''}
                onChange={handleCustomTextChange}
              />
            </TranslatableInput>
          </PanelEditorSection>
          <PanelEditorSection>
            <PanelEditorLabel as="label" htmlFor={externalUrlInputId}>
              {intl.formatMessage(messages.linkUrl)}
            </PanelEditorLabel>
            <Input
              id={externalUrlInputId}
              isFullWidth
              value={config.externalUrl ?? ''}
              onChange={handleExternalUrlChange}
            />
          </PanelEditorSection>
        </>
      )}
    </>
  );
};

// Immutable config updates make prop comparison cheap: the component re-renders only when its
// panel's buttonConfig (or the grid callback) changes.
export const LinkConfigSelect = memo(LinkConfigSelectComponent);
